"use client"

import type React from "react"

import { useState, useRef, useEffect, useCallback } from "react"
import { Button } from "@/components/ui/button"
import { Slider } from "@/components/ui/slider"
import {
  Play,
  Pause,
  Volume2,
  VolumeX,
  Maximize,
  Minimize,
  SkipBack,
  SkipForward,
  Download,
  Share2,
  Loader2,
} from "lucide-react"
import { cn } from "@/lib/utils"
import { useToast } from "@/components/ui/use-toast"

interface VideoPlayerProps {
  url: string
  title?: string
  poster?: string
  className?: string
  autoPlay?: boolean
  muted?: boolean
  isMobile: boolean
  onDownload?: () => void
  onShare?: () => void
  onError?: (error: string) => void
  onLoadStart?: () => void
  onLoadEnd?: () => void
}

export function VideoPlayer({
  url,
  title = "Video",
  poster,
  className,
  autoPlay = false,
  muted = false,
  isMobile,
  onDownload,
  onShare,
  onError,
  onLoadStart,
  onLoadEnd,
}: VideoPlayerProps) {
  const { toast } = useToast()
  const videoRef = useRef<HTMLVideoElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const progressRef = useRef<HTMLDivElement>(null)
  const controlsTimeoutRef = useRef<NodeJS.Timeout>(null)

  // Video state
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [volume, setVolume] = useState(1)
  const [isMuted, setIsMuted] = useState(muted)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showControls, setShowControls] = useState(true)
  const [playbackRate, setPlaybackRate] = useState(1)
  const [isBuffering, setIsBuffering] = useState(false)
  const [bufferedRanges, setBufferedRanges] = useState<{ start: number; end: number }[]>([])

  // Format time helper
  const formatTime = useCallback((seconds: number): string => {
    if (isNaN(seconds) || !isFinite(seconds)) return "0:00"
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, "0")}`
  }, [])

  // Update buffered ranges
  const updateBufferedRanges = useCallback(() => {
    if (!videoRef.current) return

    const buffered = videoRef.current.buffered
    const ranges: { start: number; end: number }[] = []

    for (let i = 0; i < buffered.length; i++) {
      ranges.push({
        start: buffered.start(i),
        end: buffered.end(i),
      })
    }

    setBufferedRanges(ranges)
  }, [])

  // Toggle play/pause
  const togglePlayPause = useCallback(async () => {
    if (!videoRef.current) return

    try {
      if (isPlaying) {
        videoRef.current.pause()
      } else {
        await videoRef.current.play()
      }
    } catch (error) {
      console.log(url)
      console.error("Playback error:", error)
      toast({
        title: "Playback Error",
        description: "Failed to play video. Please try again.",
        variant: "destructive",
      })
    }
  }, [isPlaying, toast])

  // Seek to specific time
  const seekTo = useCallback(
    (time: number) => {
      if (!videoRef.current) return
      videoRef.current.currentTime = Math.max(0, Math.min(time, duration))
    },
    [duration],
  )

  // Handle progress bar click
  const handleProgressClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!progressRef.current || !duration) return

      const rect = progressRef.current.getBoundingClientRect()
      const percent = (e.clientX - rect.left) / rect.width
      const newTime = percent * duration
      seekTo(newTime)
    },
    [duration, seekTo],
  )

  // Skip forward/backward
  const skipForward = useCallback(() => seekTo(currentTime + 10), [currentTime, seekTo])
  const skipBackward = useCallback(() => seekTo(currentTime - 10), [currentTime, seekTo])

  // Volume controls
  const toggleMute = useCallback(() => {
    if (!videoRef.current) return

    const newMuted = !isMuted
    videoRef.current.muted = newMuted
    setIsMuted(newMuted)
  }, [isMuted])

  const handleVolumeChange = useCallback(
    (newVolume: number[]) => {
      if (!videoRef.current) return

      const vol = newVolume[0]
      videoRef.current.volume = vol
      setVolume(vol)

      if (vol === 0) {
        setIsMuted(true)
        videoRef.current.muted = true
      } else if (isMuted) {
        setIsMuted(false)
        videoRef.current.muted = false
      }
    },
    [isMuted],
  )

  // Fullscreen controls
  const toggleFullscreen = useCallback(async () => {
    if (!containerRef.current) return

    try {
      if (!isFullscreen) {
        if (containerRef.current.requestFullscreen) {
          await containerRef.current.requestFullscreen()
        }
      } else {
        if (document.exitFullscreen) {
          await document.exitFullscreen()
        }
      }
    } catch (error) {
      console.error("Fullscreen error:", error)
    }
  }, [isFullscreen])

  // Playback rate control
  const changePlaybackRate = useCallback((rate: number) => {
    if (!videoRef.current) return
    videoRef.current.playbackRate = rate
    setPlaybackRate(rate)
  }, [])

  // Controls visibility
  const showControlsTemporarily = useCallback(() => {
    setShowControls(true)

    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current)
    }

    controlsTimeoutRef.current = setTimeout(() => {
      if (isPlaying) {
        setShowControls(false)
      }
    }, 3000)
  }, [isPlaying])

  // Keyboard shortcuts
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!videoRef.current) return

      switch (e.code) {
        case "Space":
          e.preventDefault()
          togglePlayPause()
          break
        case "ArrowLeft":
          e.preventDefault()
          skipBackward()
          break
        case "ArrowRight":
          e.preventDefault()
          skipForward()
          break
        case "ArrowUp":
          e.preventDefault()
          handleVolumeChange([Math.min(1, volume + 0.1)])
          break
        case "ArrowDown":
          e.preventDefault()
          handleVolumeChange([Math.max(0, volume - 0.1)])
          break
        case "KeyM":
          e.preventDefault()
          toggleMute()
          break
        case "KeyF":
          e.preventDefault()
          toggleFullscreen()
          break
      }
    },
    [togglePlayPause, skipBackward, skipForward, handleVolumeChange, volume, toggleMute, toggleFullscreen],
  )

  // Video event handlers
  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const handleLoadStart = () => {
      setIsLoading(true)
      setError(null)
      onLoadStart?.()
    }

    const handleLoadedMetadata = () => {
      setDuration(video.duration)
      setIsLoading(false)
      onLoadEnd?.()
    }

    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime)
      updateBufferedRanges()
    }

    const handlePlay = () => setIsPlaying(true)
    const handlePause = () => setIsPlaying(false)

    const handleWaiting = () => setIsBuffering(true)
    const handleCanPlay = () => setIsBuffering(false)

    const handleError = () => {
      const errorMessage = "Failed to load video"
      setError(errorMessage)
      setIsLoading(false)
      onError?.(errorMessage)
    }

    const handleVolumeChange = () => {
      setVolume(video.volume)
      setIsMuted(video.muted)
    }

    const handleProgress = () => updateBufferedRanges()

    video.addEventListener("loadstart", handleLoadStart)
    video.addEventListener("loadedmetadata", handleLoadedMetadata)
    video.addEventListener("timeupdate", handleTimeUpdate)
    video.addEventListener("play", handlePlay)
    video.addEventListener("pause", handlePause)
    video.addEventListener("waiting", handleWaiting)
    video.addEventListener("canplay", handleCanPlay)
    video.addEventListener("error", handleError)
    video.addEventListener("volumechange", handleVolumeChange)
    video.addEventListener("progress", handleProgress)

    return () => {
      video.removeEventListener("loadstart", handleLoadStart)
      video.removeEventListener("loadedmetadata", handleLoadedMetadata)
      video.removeEventListener("timeupdate", handleTimeUpdate)
      video.removeEventListener("play", handlePlay)
      video.removeEventListener("pause", handlePause)
      video.removeEventListener("waiting", handleWaiting)
      video.removeEventListener("canplay", handleCanPlay)
      video.removeEventListener("error", handleError)
      video.removeEventListener("volumechange", handleVolumeChange)
      video.removeEventListener("progress", handleProgress)
    }
  }, [updateBufferedRanges, onError, onLoadStart, onLoadEnd])

  // Fullscreen change handler
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement)
    }

    document.addEventListener("fullscreenchange", handleFullscreenChange)
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange)
  }, [])

  // Keyboard event listener
  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [handleKeyDown])

  // Mouse movement handler for controls
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const handleMouseMove = () => showControlsTemporarily()
    const handleMouseLeave = () => {
      if (isPlaying) {
        setShowControls(false)
      }
    }

    container.addEventListener("mousemove", handleMouseMove)
    container.addEventListener("mouseleave", handleMouseLeave)

    return () => {
      container.removeEventListener("mousemove", handleMouseMove)
      container.removeEventListener("mouseleave", handleMouseLeave)
    }
  }, [isPlaying, showControlsTemporarily])

  // Cleanup timeout
  useEffect(() => {
    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current)
      }
    }
  }, [])

  if (error) {
    return (
      <div className={cn("relative bg-black rounded-lg overflow-hidden", className)}>
        <div className="aspect-video flex items-center justify-center">
          <div className="text-center text-white">
            <div className="text-red-400 mb-2">⚠️</div>
            <p className="text-sm">{error}</p>
            <Button
              size="sm"
              variant="outline"
              className="mt-2 bg-transparent"
              onClick={() => {
                setError(null)
                setIsLoading(true)
                videoRef.current?.load()
              }}
            >
              Retry
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      ref={containerRef}
      className={cn(
        "relative bg-black rounded-lg overflow-hidden group",
        isFullscreen && "fixed inset-0 z-50 rounded-none",
        className,
      )}
      onDoubleClick={toggleFullscreen}
    >
      {/* Video Element */}
      <video
        ref={videoRef}
        src={url}
        poster={poster}
        className="w-full h-full object-contain"
        autoPlay={autoPlay}
        muted={muted}
        playsInline
        preload="metadata"
      />

      {/* Loading Overlay */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50">
          <div className="text-center text-white">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
            <p className="text-sm">Loading video...</p>
          </div>
        </div>
      )}

      {/* Buffering Indicator */}
      {isBuffering && !isLoading && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="bg-black/70 rounded-full p-3">
            <Loader2 className="h-6 w-6 animate-spin text-white" />
          </div>
        </div>
      )}

      {/* Center Play Button */}
      {!isPlaying && !isLoading && (
        <div className="absolute inset-0 flex items-center justify-center z-[9999]">
          <Button
            size="lg"
            onClick={togglePlayPause}
            className="bg-black/70 hover:bg-black/80 text-white rounded-full w-16 h-16 p-0"
          >
            <Play className="h-8 w-8 ml-1" />
          </Button>
        </div>
      )}

      {/* Controls Overlay */}
      <div
        className={cn(
          "absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/40 transition-opacity duration-300",
          showControls || !isPlaying ? "opacity-100" : "opacity-0",
        )}
      >
        {/* Top Controls */}
        <div className="absolute top-0 left-0 right-0 p-4 flex items-center justify-between">
          <h3 className="text-white font-medium truncate">{title}</h3>
          <div className="flex items-center gap-2">
            {onShare && (
              <Button size="sm" variant="ghost" onClick={onShare} className="text-white hover:bg-white/20">
                <Share2 className="h-4 w-4" />
              </Button>
            )}
            {onDownload && (
              <Button size="sm" variant="ghost" onClick={onDownload} className="text-white hover:bg-white/20">
                <Download className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Bottom Controls */}
        <div className="absolute bottom-0 left-0 right-0 p-4">
          {/* Progress Bar */}
          <div className="mb-4">
            <div
              ref={progressRef}
              className="relative h-1 bg-white/30 rounded-full cursor-pointer hover:h-2 transition-all"
              onClick={handleProgressClick}
            >
              {/* Buffered Progress */}
              {bufferedRanges.map((range, index) => (
                <div
                  key={index}
                  className="absolute top-0 h-full bg-white/50 rounded-full"
                  style={{
                    left: `${(range.start / duration) * 100}%`,
                    width: `${((range.end - range.start) / duration) * 100}%`,
                  }}
                />
              ))}

              {/* Current Progress */}
              <div
                className="absolute top-0 h-full bg-[#BFA2DB] rounded-full transition-all"
                style={{ width: `${(currentTime / duration) * 100}%` }}
              />

              {/* Progress Handle */}
              <div
                className="absolute top-1/2 w-3 h-3 bg-[#BFA2DB] rounded-full transform -translate-y-1/2 opacity-0 hover:opacity-100 transition-opacity"
                style={{ left: `${(currentTime / duration) * 100}%` }}
              />
            </div>
          </div>

          {/* Control Buttons */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button size="sm" variant="ghost" onClick={togglePlayPause} className="text-white hover:bg-white/20">
                {isPlaying ? <Pause className="h-5 w-5" /> : <></>}
              </Button>

              <Button size="sm" variant="ghost" onClick={skipBackward} className="text-white hover:bg-white/20">
                <SkipBack className="h-4 w-4" />
              </Button>

              <Button size="sm" variant="ghost" onClick={skipForward} className="text-white hover:bg-white/20">
                <SkipForward className="h-4 w-4" />
              </Button>

              <div className="flex items-center gap-2 ml-2">
                <Button size="sm" variant="ghost" onClick={toggleMute} className="text-white hover:bg-white/20">
                  {isMuted || volume === 0 ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                </Button>

                <div className="w-20">
                  {!isMobile && (<Slider
                    value={[isMuted ? 0 : volume]}
                    max={1}
                    step={0.1}
                    onValueChange={handleVolumeChange}
                    className="cursor-pointer"
                  />)}
                </div>
              </div>

              <div className="text-white text-sm ml-4">
                {formatTime(currentTime)} / {formatTime(duration)}
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* Playback Speed */}
              <select
                value={playbackRate}
                onChange={(e) => changePlaybackRate(Number(e.target.value))}
                className="bg-transparent text-white text-sm border border-white/30 rounded px-2 py-1"
              >
                <option value={0.5} className="bg-black">
                  0.5x
                </option>
                <option value={0.75} className="bg-black">
                  0.75x
                </option>
                <option value={1} className="bg-black">
                  1x
                </option>
                <option value={1.25} className="bg-black">
                  1.25x
                </option>
                <option value={1.5} className="bg-black">
                  1.5x
                </option>
                <option value={2} className="bg-black">
                  2x
                </option>
              </select>

              <Button size="sm" variant="ghost" onClick={toggleFullscreen} className="text-white hover:bg-white/20">
                {isFullscreen ? <Minimize className="h-4 w-4" /> : <Maximize className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Keyboard Shortcuts Help */}
      {isFullscreen && !isMobile && (
        <div className="absolute top-4 right-4 bg-black/70 rounded-lg p-3 text-white text-xs">
          <div className="space-y-1">
            <div>Space: Play/Pause</div>
            <div>← →: Skip 10s</div>
            <div>↑ ↓: Volume</div>
            <div>M: Mute</div>
            <div>F: Fullscreen</div>
          </div>
        </div>
      )}
    </div>
  )
}
